import { ReactNode } from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FormikErrors } from 'formik';
import { Field } from 'types/api/Tenant/AROKMS/DisplayTableTypes';

interface DataInputValidationAlertProps {
  errors: FormikErrors<{ [key: string]: any }>;
  fields: Field[];
  submitCount: number;
}

interface DataInputValidationFieldProps {
  children: ReactNode;
  error: unknown;
  touched: unknown;
}

export interface DataInputValidationMessage {
  fieldName: string;
  message: string;
}

function flattenValidationError(error: unknown): string[] {
  if (typeof error === 'string') {
    return [error];
  }

  if (Array.isArray(error)) {
    return error.flatMap(flattenValidationError);
  }

  if (error && typeof error === 'object') {
    return Object.values(error).flatMap(flattenValidationError);
  }

  return [];
}

export function getDataInputValidationMessages(
  errors: FormikErrors<{ [key: string]: any }>,
  fields: Field[]
): DataInputValidationMessage[] {
  return Object.entries(errors).flatMap(([fieldName, error]) => {
    const field = fields.find((item) => item.name === fieldName || item.id === fieldName);
    const fieldLabel = field?.realLabel || field?.label || fieldName;

    return flattenValidationError(error).map((message) => ({
      fieldName,
      message: message.toLowerCase().includes(fieldLabel.toLowerCase())
        ? message
        : `${fieldLabel}: ${message}`,
    }));
  });
}

export function DataInputValidationAlert({ errors, fields, submitCount }: DataInputValidationAlertProps) {
  const validationMessages = getDataInputValidationMessages(errors, fields);

  if (submitCount === 0 || validationMessages.length === 0) {
    return null;
  }

  return (
    <Alert
      severity='error'
      variant='outlined'
      sx={(theme) => ({
        mt: 2,
        mb: 2,
        py: 0.75,
        alignItems: 'flex-start',
        borderColor: alpha(theme.palette.error.main, 0.3),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.error.main, 0.025),
        color: theme.palette.text.primary,
        '& .MuiAlert-icon': {
          mt: 0.25,
          color: theme.palette.error.main,
        },
        '& .MuiAlert-message': {
          width: '100%',
        },
      })}
    >
      <AlertTitle sx={{ mb: 0.5, fontWeight: 700 }}>Please review the highlighted fields</AlertTitle>
      <Box
        component='ul'
        sx={{
          m: 0,
          pl: 2.25,
          display: 'grid',
          gap: 0.5,
          color: 'text.secondary',
          '& li': { lineHeight: 1.45 },
          '& li::marker': { color: 'error.main' },
        }}
      >
        {validationMessages.map(({ fieldName, message }, index) => (
          <li key={`${fieldName}-${index}`}>{message}</li>
        ))}
      </Box>
    </Alert>
  );
}

export function DataInputValidationField({ children, error, touched }: DataInputValidationFieldProps) {
  const isInvalid = Boolean(touched && error);

  return (
    <Box
      data-invalid={isInvalid || undefined}
      sx={(theme) => ({
        transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isInvalid && {
          position: 'relative',
          mt: 1.5,
          mb: 0.5,
          px: 1.5,
          pb: 1.5,
          border: '1px solid',
          borderColor: alpha(theme.palette.error.main, 0.3),
          borderRadius: 2,
          bgcolor: alpha(theme.palette.error.main, 0.025),
          '&:focus-within': {
            borderColor: alpha(theme.palette.error.main, 0.5),
            boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.06)}`,
          },
          '& .MuiInputBase-root:not(.Mui-disabled)': {
            bgcolor: theme.palette.background.paper,
          },
          '& .MuiFormHelperText-root.Mui-error': {
            mt: 0.75,
            fontWeight: 600,
          },
        }),
      })}
    >
      {children}
    </Box>
  );
}
